import client from "../client/client";
import { TOKEN } from "../utils/constants";

const USER_PLAN_BASE_PATH = "/user-api/user-plan";
const USER_SEARCH_BASE_PATH = "/user-api/search";
const USER_BASE_PATH = "/user-api/user";

const toPositiveInt = (value) => {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const parsed = parseInt(trimmed, 10);
      if (parsed > 0) return parsed;
    }
  }

  return null;
};

const tryExtractIdFromObject = (value) => {
  if (!value || typeof value !== "object") return null;

  const candidates = [
    value.client_user_id,
    value.user_id,
    value.userID,
    value.idUser,
    value.id,
  ];

  for (const candidate of candidates) {
    const parsed = toPositiveInt(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (_error) {
    return null;
  }
};

const getSessionUserId = () => {
  const storageCandidates = [
    sessionStorage.getItem("idUser"),
    sessionStorage.getItem("User"),
  ];

  for (const rawCandidate of storageCandidates) {
    const parsedDirect = toPositiveInt(rawCandidate);
    if (parsedDirect) return parsedDirect;

    if (typeof rawCandidate === "string") {
      try {
        const parsedJson = JSON.parse(rawCandidate);
        const nestedId = tryExtractIdFromObject(parsedJson);
        if (nestedId) return nestedId;
      } catch (_error) {
        // No es JSON, continuamos con el siguiente candidato.
      }
    }
  }

  const token = sessionStorage.getItem(TOKEN);
  const tokenPayload = decodeJwtPayload(token);
  const tokenId = tryExtractIdFromObject(tokenPayload);
  if (tokenId) return tokenId;

  throw new Error("No hay un user_id válido en sesión");
};

const withUserId = (payload = {}) => {
  const payloadUserId = toPositiveInt(payload?.user_id);
  const fallbackPayloadId = toPositiveInt(payload?.id);
  const resolvedUserId = payloadUserId ?? fallbackPayloadId;

  if (!resolvedUserId) {
    throw new Error("Payload inválido: se requiere user_id");
  }

  return {
    ...payload,
    user_id: resolvedUserId,
  };
};

/**
 * Extrae una colección desde distintos formatos de respuesta de backend.
 * @param {unknown} data
 * @returns {Array}
 */
const extractItems = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  const collectionKeys = [
    "items",
    "data",
    "content",
    "rows",
    "results",
    "result",
    "users",
    "userPlans",
    "user_plans",
    "payload",
  ];

  for (const key of collectionKeys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  // Algunos endpoints anidan la lista dentro de un objeto contenedor:
  // { data: { items: [...] } }, { payload: { rows: [...] } }, etc.
  for (const key of collectionKeys) {
    const nested = data?.[key];
    if (nested && typeof nested === "object") {
      const nestedItems = extractItems(nested);
      if (nestedItems.length > 0) return nestedItems;
    }
  }

  // Fallback: si existe cualquier propiedad array, usar la primera.
  if (typeof data === "object") {
    const firstArray = Object.values(data).find((value) => Array.isArray(value));
    if (Array.isArray(firstArray)) return firstArray;
  }

  // Algunos endpoints pueden devolver directamente un solo registro
  // en lugar de una colección.
  if (data && typeof data === "object") {
    return [data];
  }

  return [];
};

/**
 * Extrae un objeto desde distintos formatos de respuesta de backend.
 * @param {unknown} data
 * @returns {Object}
 */
const extractObject = (data) => {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
      return data.data;
    }
    return data;
  }
  return {};
};

/**
 * GET /api/user-api/search
 */
const searchUsers = async (params = {}) => {
  const requestParams = {
    page: params.page ?? 1,
    page_size: params.page_size ?? 100,
    includePermissions: params.includePermissions ?? false,
  };

  if (params.active !== undefined && params.active !== null) {
    requestParams.active = params.active;
  }

  const resp = await client.get(USER_SEARCH_BASE_PATH, {
    params: requestParams,
  });

  return {
    raw: resp.data,
    items: extractItems(resp.data),
  };
};

/**
 * GET /api/user-api/user-plan?user_id={userId}
 */
const getUserPlans = async (userId) => {
  const resolvedUserId = toPositiveInt(userId);
  if (!resolvedUserId) {
    throw new Error("userId inválido");
  }
  const resp = await client.get(USER_PLAN_BASE_PATH, {
    params: { user_id: resolvedUserId },
  });
  return {
    raw: resp.data,
    items: extractItems(resp.data),
  };
};

/**
 * POST /api/user-api/user-plan
 */
const createUserPlan = async (payload) => {
  const resp = await client.post(USER_PLAN_BASE_PATH, withUserId(payload));
  return extractObject(resp.data);
};

/**
 * PUT /api/user-api/user-plan
 */
const updateUserPlan = async (payload) => {
  const resp = await client.put(USER_PLAN_BASE_PATH, withUserId(payload));
  return extractObject(resp.data);
};

/**
 * GET /api/user-api/user-plan/{id}
 */
const getUserPlanById = async (id) => {
  const resp = await client.get(`${USER_PLAN_BASE_PATH}/${id}`, {
    params: { user_id: getSessionUserId() },
  });
  return extractObject(resp.data);
};

/**
 * POST /api/user-api/user-plan/renew
 */
const renewUserPlan = async (payload) => {
  const resp = await client.post(
    `${USER_PLAN_BASE_PATH}/renew`,
    withUserId(payload)
  );
  return extractObject(resp.data);
};

/**
 * GET /api/user-api/user-plan/check-expiration
 */
const checkExpiration = async () => {
  const resp = await client.get(`${USER_PLAN_BASE_PATH}/check-expiration`, {
    params: { user_id: getSessionUserId() },
  });
  return {
    raw: resp.data,
    items: extractItems(resp.data),
  };
};

/**
 * POST /api/user-api/user-plan/delete
 */
const deleteUserPlan = async (payload) => {
  const resp = await client.post(
    `${USER_PLAN_BASE_PATH}/delete`,
    withUserId(payload)
  );
  return extractObject(resp.data);
};

/**
 * PATCH /api/user-api/user/{userId}/deactivate
 */
const deactivateUser = async (userId) => {
  const resolvedUserId = toPositiveInt(userId);
  if (!resolvedUserId) {
    throw new Error("userId inválido");
  }
  const resp = await client.patch(
    `${USER_BASE_PATH}/${resolvedUserId}/deactivate`
  );
  return extractObject(resp.data);
};

const userPlanService = {
  searchUsers,
  getUserPlans,
  createUserPlan,
  updateUserPlan,
  getUserPlanById,
  renewUserPlan,
  checkExpiration,
  deleteUserPlan,
  deactivateUser,
};

export default userPlanService;
